import { useState, type SVGProps } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";
import type { CaseCard } from "@/services/navigator/case-study-service";

// Figma "Case Card" (목록 203:9111 · 상세 더보기 203:10125): 대표 이미지 + 어두운 그라데이션 위에 에이전트 ·
// 대표 성과, 아래 제목 · 산업. grid = 3열 목록(이미지 164), wide = 상세 더보기(이미지 219).

// Figma industry icons (203:9170 …), 16 x 16 stroke #6A6F77
const INDUSTRY_ICONS: Record<string, string[]> = {
  ship: [
    "M2 10.667H14L12.5333 13.467C12.4498 13.6253 12.3254 13.7583 12.1729 13.8521C12.0205 13.946 11.8456 13.9971 11.6667 14.0003H4.33333C4.15436 13.9971 3.97953 13.946 3.82709 13.8521C3.67464 13.7583 3.55017 13.6253 3.46667 13.467L2 10.667Z",
    "M4 10.6663V7.33301H12V10.6663",
    "M8 7.33366V2.66699",
    "M8 3L10.6667 4.2L8 5.33333",
  ],
  factory: [
    "M2 14.0003V6.66699L5.33333 8.66699V6.66699L8.66667 8.66699V4.66699L14 7.33366V14.0003H2Z",
    "M4.66797 11.333H6.0013M8.0013 11.333H9.33464M11.3346 11.333H12.668",
  ],
  bank: [
    "M2 6.00033L8 2.66699L14 6.00033",
    "M3.33203 6.66699V12.0003M6.33203 6.66699V12.0003M9.66536 6.66699V12.0003M12.6654 6.66699V12.0003",
    "M2 13.667H14",
  ],
  truck: [
    "M1.33203 4H8.66536V10.6667H1.33203V4Z",
    "M8.66797 6.66699H11.3346L13.3346 8.80033V10.667H8.66797",
    "M4.0013 13.3337C4.73768 13.3337 5.33464 12.7367 5.33464 12.0003C5.33464 11.2639 4.73768 10.667 4.0013 10.667C3.26492 10.667 2.66797 11.2639 2.66797 12.0003C2.66797 12.7367 3.26492 13.3337 4.0013 13.3337Z",
    "M11.3333 13.3337C12.0697 13.3337 12.6667 12.7367 12.6667 12.0003C12.6667 11.2639 12.0697 10.667 11.3333 10.667C10.597 10.667 10 11.2639 10 12.0003C10 12.7367 10.597 13.3337 11.3333 13.3337Z",
  ],
  flask: [
    "M6 2H10",
    "M6.6685 2V6L3.20183 12.0667C3.10131 12.2657 3.05229 12.4867 3.05924 12.7095C3.0662 12.9323 3.12892 13.1498 3.24166 13.3422C3.35441 13.5345 3.51357 13.6955 3.7046 13.8104C3.89564 13.9253 4.11243 13.9905 4.33517 14H11.6685C11.8912 13.9905 12.108 13.9253 12.2991 13.8104C12.4901 13.6955 12.6493 13.5345 12.762 13.3422C12.8747 13.1498 12.9375 12.9323 12.9444 12.7095C12.9514 12.4867 12.9024 12.2657 12.8018 12.0667L9.33517 6V2",
    "M4.93359 10H11.0669",
  ],
  bag: [
    "M3.33203 5.33301H12.6654L11.932 13.9997H4.06536L3.33203 5.33301Z",
    "M6 5.33301V4.33301C6 3.80257 6.21071 3.29387 6.58579 2.91879C6.96086 2.54372 7.46957 2.33301 8 2.33301C8.53043 2.33301 9.03914 2.54372 9.41421 2.91879C9.78929 3.29387 10 3.80257 10 4.33301V5.33301",
  ],
  building: [
    "M3.33203 14V6H12.6654V14",
    "M2 14H14",
    "M6 14.0003V10.667H10V14.0003",
    "M8 6V2L10.6667 3L8 4",
    "M5.33203 8.33301H6.33203M9.66536 8.33301H10.6654",
  ],
};

// 카탈로그 분류(IVC-01) 산업 → 아이콘. 스테이징 실데이터가 이 코드로 와요 (Figma ver.7 목록 카드의 7종에 맞춤)
const IVC_ICONS: Record<string, keyof typeof INDUSTRY_ICONS> = {
  "IVC-01-01": "bank", // 금융
  "IVC-01-02": "factory", // 반도체·전자
  "IVC-01-04": "factory", // 자동차·모빌리티
  "IVC-01-05": "factory", // 중공업·EPC (조선·해양은 아래에서 ship)
  "IVC-01-06": "factory", // 화학·소재
  "IVC-01-08": "truck", // 물류·유통·무역
  "IVC-01-09": "bag", // 소비재·리테일
  "IVC-01-11": "flask", // 헬스케어 (제약·바이오)
};

// 산업 코드 → 아이콘 (Figma 7종 중 가장 가까운 것, 시안에 없는 산업은 건물 — 가정 A30)
function industryIcon(code: string | null, rootCode: string | null, label?: string): keyof typeof INDUSTRY_ICONS {
  if (code === "IND-02-04" || code === "IVC-01-05-01") return "ship";
  if (code === "IND-06-05") return "truck";
  const ivc = rootCode ? IVC_ICONS[rootCode] : undefined;
  if (ivc) return ivc;
  // 산업 노드 없이 직접 적은 산업명 ("제조" 등)
  if (!code && label && /^제조/.test(label)) return "factory";
  switch (rootCode) {
    case "IND-01":
      return "bank";
    case "IND-02":
      return "factory";
    case "IND-03":
      return "bag";
    case "IND-05":
      return "flask";
    default:
      return "building";
  }
}

export function NvIndustryIcon({
  code,
  rootCode,
  label,
  ...props
}: { code: string | null; rootCode: string | null; label?: string } & SVGProps<SVGSVGElement>) {
  return (
    <svg width={16} height={16} viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      {INDUSTRY_ICONS[industryIcon(code, rootCode, label)].map((d) => (
        <path key={d} d={d} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

// Figma 20 x 20 arrow in CTA buttons (203:9302 white · 203:9986 brand)
export function NvArrowRightIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" fill="none" aria-hidden {...props}>
      <path d="M3 10H16M11 15L16 10L11 5" stroke="currentColor" strokeWidth={1.33333} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// Top-to-bottom dim from Figma "Overlay": #0E1014 55% → 28% (42%) → 72%
const OVERLAY = "linear-gradient(180deg, rgba(14,16,20,0.55) 0%, rgba(14,16,20,0.28) 42%, rgba(14,16,20,0.72) 100%)";
// 대표 수치가 없는 카드(ver.7 목록 · 제안 17): 수치 영역이 없으니 아래를 덜 어둡게 해서 이미지가 보이게 해요
const OVERLAY_NO_METRIC = "linear-gradient(180deg, rgba(14,16,20,0.3) 0%, rgba(14,16,20,0.2) 42%, rgba(14,16,20,0.56) 100%)";

// 백엔드가 표지를 안 올린 사례에 내려주는 기본 표지(단색에 가까운 어두운 플레이스홀더) 표식
const DEFAULT_COVER_MARK = "/case-study/default-cover";

export function isCaseDefaultCover(url: string | null | undefined) {
  return !url || url.includes(DEFAULT_COVER_MARK);
}

// 사례 대표 이미지 (제안 17 · 상세 규칙): 등록된 이미지를 보여주고, 미등록 · 기본 표지 · 불러오기 실패면 어두운 빈 박스 대신
// 산업 아이콘을 얹은 중간 톤 그라데이션을 깔아요. 산업별 기본 사진 세트는 아직 전달되지 않았어요(가정 A31).
// 부모는 relative + overflow-hidden 이어야 해요.
export function NvCaseCover({ url, industry }: { url: string | null; industry: CaseCard["industry"] }) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  if (!isCaseDefaultCover(url) && failedUrl !== url) {
    return <img src={url!} alt="" className="absolute inset-0 h-full w-full object-cover" onError={() => setFailedUrl(url)} />;
  }
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden bg-gradient-to-br from-[#8A9099] to-[#565B65]">
      <NvIndustryIcon
        code={industry.code}
        rootCode={industry.root_code}
        label={industry.label}
        width={120}
        height={120}
        className="absolute -bottom-3 right-6 text-white/[0.22] [&>path]:[stroke-width:0.7]"
      />
    </div>
  );
}

export interface NvCaseCardProps {
  item: CaseCard;
  size?: "grid" | "wide";
  // Where the card goes; when absent the card is a button (권한 안내 팝업용)
  to?: string;
  onClick?: () => void;
  className?: string;
  // Figma ver.7 + 제안 17 "Case Study 목록 실데이터 규칙": 브랜드명 1줄 말줄임(+ 마우스 오버로 전체명) · 로고는 30px 흰 박스에 비율 유지 ·
  // 대표 수치가 없으면 수치 영역을 숨기고 이미지 + 브랜드만 · 이미지가 없으면 산업 기본 이미지 · 제목 단어 단위 줄바꿈(2줄 말줄임).
  // 기존 사용처는 그대로 두려고 켜는 쪽에서만 지정해요.
  realData?: boolean;
}

function NvCaseCard({ item, size = "grid", to, onClick, className, realData = false }: NvCaseCardProps) {
  const body = (
    <>
      <div className={cn("relative w-full overflow-hidden bg-nv-surface-inverse", size === "grid" ? "h-[164px]" : "h-[219px]")}>
        {realData ? (
          <NvCaseCover url={item.cover_image_url} industry={item.industry} />
        ) : (
          item.cover_image_url && <img src={item.cover_image_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div className="absolute inset-0" style={{ background: realData && !item.card_metric_value ? OVERLAY_NO_METRIC : OVERLAY }} />
        {item.agent && (
          <div className={cn("absolute top-[18px] flex items-center gap-[10px]", realData ? "inset-x-5" : "left-5")}>
            {item.agent.logo_url ? (
              realData ? (
                <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-nv-pill bg-white p-[3px]">
                  <img src={item.agent.logo_url} alt="" className="max-h-full max-w-full object-contain" />
                </span>
              ) : (
                <img src={item.agent.logo_url} alt="" className="h-[30px] w-[30px] rounded-nv-pill bg-nv-surface object-contain" />
              )
            ) : (
              <span aria-hidden className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-nv-pill bg-white/[0.18] text-[13px] font-bold leading-4 text-nv-text-inverse">
                {item.agent.name.trim().charAt(0).toUpperCase()}
              </span>
            )}
            <span
              title={realData ? item.agent.name : undefined}
              className={cn("text-[16px] font-bold leading-[19px] tracking-[-0.01em] text-nv-text-inverse", realData && "min-w-0 flex-1 truncate")}
            >
              {item.agent.name}
            </span>
          </div>
        )}
        {item.card_metric_value && (
          <div className="absolute bottom-4 left-5 right-5 flex flex-col gap-1">
            <span className="truncate text-[38px] font-normal leading-[40px] tracking-[-0.03em] text-nv-text-inverse">{item.card_metric_value}</span>
            {item.card_metric_label && (
              <span className={cn("truncate text-[14px] leading-[17px] tracking-[-0.01em]", realData ? "text-white" : "text-white/[0.92]")}>{item.card_metric_label}</span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-5 px-[22px] pb-5 pt-5">
        <p className={cn("line-clamp-2 text-[19px] leading-[29.4px] tracking-[-0.015em] text-nv-text-primary", realData && "break-keep")}>{item.title}</p>
        <span className="flex items-center gap-[6px] text-[14px] leading-[17px] tracking-[-0.01em] text-nv-text-secondary">
          <NvIndustryIcon code={item.industry.code} rootCode={item.industry.root_code} label={item.industry.label} />
          {item.industry.label}
        </span>
      </div>
    </>
  );
  const frame = cn(
    "flex flex-col overflow-hidden rounded-nv-card border border-nv-border bg-nv-surface text-left transition-shadow hover:shadow-[0_4px_16px_rgba(26,28,33,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand",
    className,
  );
  return to ? (
    <Link to={to} className={frame}>
      {body}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={frame}>
      {body}
    </button>
  );
}

export { NvCaseCard };
